"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.validAllocationStrategyModeTuples = exports.InvalidAllocationStrategyError = exports.workersPerPacketModes = exports.packetsPerWorkerModes = exports.packetSources = void 0;
exports.validateAllocationStrategy = validateAllocationStrategy;
exports.formatAllocationStrategyName = formatAllocationStrategyName;
/**
 * Allocation strategy for v2 tasks, stored on `task.allocation_strategy`.
 *
 * A strategy is fully described by three orthogonal axes — `packetSource`,
 * `packetsPerWorker`, and `workersPerPacket`. Of the `2 × 3 × 3 = 18`
 * cross-product cells, 12 are valid and 6 are invalid/degenerate; see
 * `work-packets-prd.md` ("Allocation Strategies") for the design.
 */
const error_values_1 = require("@karya/error-values");
/**
 * Where a work packet's structure comes from.
 *
 * - `INHERITED`: structure is inherited from a parent `data_packet`. Each
 *   microtask belongs to exactly one work packet (1:N), linked via
 *   `microtask.work_packet_id`.
 * - `COMPOSED`: packet is composed at allocation time from a microtask pool
 *   (no parent data packet). Microtasks may appear in multiple packets (M:N),
 *   linked via the `work_packet_microtask` join table.
 */
exports.packetSources = ['INHERITED', 'COMPOSED'];
/**
 * Allowed `packetsPerWorker.mode` values; mirrors the {@link PacketsPerWorker}
 * union and is exported so callers (and runtime validators) can enumerate
 * them.
 */
exports.packetsPerWorkerModes = ['ALL', 'FIXED', 'UNBOUNDED'];
/**
 * Allowed `workersPerPacket.mode` values; mirrors the {@link WorkersPerPacket}
 * union.
 */
exports.workersPerPacketModes = ['ALL', 'FIXED', 'SINGLE'];
class InvalidAllocationStrategyError extends error_values_1.CodedError {
    constructor(reason, strategy) {
        super('InvalidAllocationStrategyError', formatInvalidStrategyMessage(reason, strategy), { reason, strategy });
    }
}
exports.InvalidAllocationStrategyError = InvalidAllocationStrategyError;
function formatInvalidStrategyMessage(reason, strategy) {
    var _a, _b;
    switch (reason) {
        case 'INVALID_PACKET_SOURCE':
            return `Invalid allocation strategy: packetSource must be one of ${exports.packetSources.join(', ')} (got ${describeUnknown(strategy.packetSource)}).`;
        case 'INVALID_PACKETS_PER_WORKER_MODE':
            return `Invalid allocation strategy: packetsPerWorker.mode must be one of ${exports.packetsPerWorkerModes.join(', ')} (got ${describeUnknown((_a = strategy.packetsPerWorker) === null || _a === void 0 ? void 0 : _a.mode)}).`;
        case 'INVALID_WORKERS_PER_PACKET_MODE':
            return `Invalid allocation strategy: workersPerPacket.mode must be one of ${exports.workersPerPacketModes.join(', ')} (got ${describeUnknown((_b = strategy.workersPerPacket) === null || _b === void 0 ? void 0 : _b.mode)}).`;
        case 'COMPOSED_WITH_PACKETS_PER_WORKER_ALL':
            return 'Invalid allocation strategy: COMPOSED + packetsPerWorker=ALL is undefined (supply is unbounded over time).';
        case 'INHERITED_ALL_K_WITH_NON_ALL_M':
            return 'Invalid allocation strategy: INHERITED + packetsPerWorker=ALL requires workersPerPacket=ALL (other m values are degenerate when worker count exceeds the cap).';
        case 'INHERITED_UNBOUNDED_K_WITH_ALL_M':
            return 'Invalid allocation strategy: INHERITED + packetsPerWorker=UNBOUNDED + workersPerPacket=ALL collapses to INHERITED/ALL/ALL — use the canonical form.';
        case 'INVALID_FIXED_VALUE':
            return `Invalid allocation strategy: FIXED axes require a positive integer value (got packetsPerWorker=${describeAxis(strategy.packetsPerWorker)}, workersPerPacket=${describeAxis(strategy.workersPerPacket)}).`;
        case 'INVALID_MAX_ATTEMPTS':
            return `Invalid allocation strategy: workersPerPacket.maxAttempts must be a positive integer >= acceptance target (got workersPerPacket=${describeAxis(strategy.workersPerPacket)}).`;
    }
}
function describeUnknown(value) {
    if (typeof value === 'string')
        return JSON.stringify(value);
    if (value === null || value === undefined)
        return String(value);
    return JSON.stringify(value);
}
function describeAxis(axis) {
    if (axis.mode === 'FIXED') {
        const maxAttempts = 'maxAttempts' in axis ? axis.maxAttempts : undefined;
        return maxAttempts !== undefined
            ? `FIXED(value=${axis.value}, maxAttempts=${maxAttempts})`
            : `FIXED(${axis.value})`;
    }
    if (axis.mode === 'SINGLE' && 'maxAttempts' in axis && axis.maxAttempts !== undefined) {
        return `SINGLE(maxAttempts=${axis.maxAttempts})`;
    }
    return axis.mode;
}
/**
 * Validate a {@link WorkPacketAllocationStrategy} against the 18-cell matrix.
 *
 * Returns `Ok(strategy)` for the 12 valid combinations and `Err` with an
 * {@link InvalidAllocationStrategyError} carrying a machine-friendly `reason`
 * for the 6 invalid/degenerate combinations. Additionally rejects:
 *
 * - `packetSource` outside {@link packetSources} (`INVALID_PACKET_SOURCE`),
 *   `packetsPerWorker.mode` outside {@link packetsPerWorkerModes}
 *   (`INVALID_PACKETS_PER_WORKER_MODE`), or `workersPerPacket.mode` outside
 *   {@link workersPerPacketModes} (`INVALID_WORKERS_PER_PACKET_MODE`). The
 *   TypeScript type is a structural hint; at runtime (e.g. parsed JSON) any
 *   string can show up, so these literal-membership checks run first.
 * - `FIXED` axes whose `value` is not a positive integer
 *   (`INVALID_FIXED_VALUE`); structurally valid but operationally meaningless.
 * - `workersPerPacket.maxAttempts`, when set, that is not a positive integer
 *   `>=` the acceptance target (`value` for `FIXED`, `1` for `SINGLE`)
 *   (`INVALID_MAX_ATTEMPTS`); a `maxAttempts` below the target is unsatisfiable
 *   by construction.
 *
 * This is intentionally pure: no I/O, no DB access, no logging. The caller
 * decides how to surface the error (e.g., the v2 task-creation handler in PR 9
 * will translate it to a 400).
 */
function validateAllocationStrategy(strategy) {
    const { packetSource, packetsPerWorker, workersPerPacket } = strategy;
    if (!isKnownPacketSource(packetSource)) {
        return (0, error_values_1.err)(new InvalidAllocationStrategyError('INVALID_PACKET_SOURCE', strategy));
    }
    if (!isKnownPacketsPerWorkerMode(packetsPerWorker === null || packetsPerWorker === void 0 ? void 0 : packetsPerWorker.mode)) {
        return (0, error_values_1.err)(new InvalidAllocationStrategyError('INVALID_PACKETS_PER_WORKER_MODE', strategy));
    }
    if (!isKnownWorkersPerPacketMode(workersPerPacket === null || workersPerPacket === void 0 ? void 0 : workersPerPacket.mode)) {
        return (0, error_values_1.err)(new InvalidAllocationStrategyError('INVALID_WORKERS_PER_PACKET_MODE', strategy));
    }
    if (!hasValidFixedValue(packetsPerWorker) || !hasValidFixedValue(workersPerPacket)) {
        return (0, error_values_1.err)(new InvalidAllocationStrategyError('INVALID_FIXED_VALUE', strategy));
    }
    if (!hasValidMaxAttempts(workersPerPacket)) {
        return (0, error_values_1.err)(new InvalidAllocationStrategyError('INVALID_MAX_ATTEMPTS', strategy));
    }
    if (packetSource === 'COMPOSED' && packetsPerWorker.mode === 'ALL') {
        return (0, error_values_1.err)(new InvalidAllocationStrategyError('COMPOSED_WITH_PACKETS_PER_WORKER_ALL', strategy));
    }
    if (packetSource === 'INHERITED' && packetsPerWorker.mode === 'ALL' && workersPerPacket.mode !== 'ALL') {
        return (0, error_values_1.err)(new InvalidAllocationStrategyError('INHERITED_ALL_K_WITH_NON_ALL_M', strategy));
    }
    if (packetSource === 'INHERITED' && packetsPerWorker.mode === 'UNBOUNDED' && workersPerPacket.mode === 'ALL') {
        return (0, error_values_1.err)(new InvalidAllocationStrategyError('INHERITED_UNBOUNDED_K_WITH_ALL_M', strategy));
    }
    return (0, error_values_1.ok)(strategy);
}
function isKnownPacketSource(value) {
    return typeof value === 'string' && exports.packetSources.includes(value);
}
function isKnownPacketsPerWorkerMode(value) {
    return typeof value === 'string' && exports.packetsPerWorkerModes.includes(value);
}
function isKnownWorkersPerPacketMode(value) {
    return typeof value === 'string' && exports.workersPerPacketModes.includes(value);
}
function hasValidFixedValue(axis) {
    if (axis.mode !== 'FIXED')
        return true;
    return Number.isInteger(axis.value) && axis.value > 0;
}
function hasValidMaxAttempts(axis) {
    if (axis.mode === 'ALL')
        return true;
    if (axis.maxAttempts === undefined)
        return true;
    if (!Number.isInteger(axis.maxAttempts) || axis.maxAttempts <= 0)
        return false;
    const target = axis.mode === 'FIXED' ? axis.value : 1;
    return axis.maxAttempts >= target;
}
// ---------------------------------------------------------------------------
// Canonical name formatter
// ---------------------------------------------------------------------------
/**
 * Canonical slash-delimited name for a strategy, e.g. `inherited / fixed / single`.
 *
 * The name format is `<packetSource> / <packetsPerWorker.mode> / <workersPerPacket.mode>`,
 * all lower-cased. This is the form used in the PRD's strategy catalog and the
 * one that operator UIs surface to users. The function is total — it produces
 * a name for every input, including the 6 invalid/degenerate cells (callers
 * that want validation should pair it with {@link validateAllocationStrategy}).
 */
function formatAllocationStrategyName(strategy) {
    return [
        strategy.packetSource.toLowerCase(),
        strategy.packetsPerWorker.mode.toLowerCase(),
        strategy.workersPerPacket.mode.toLowerCase(),
    ].join(' / ');
}
/**
 * The 12 valid mode-tuples, in the same order as the PRD's strategy catalog.
 * `FIXED` axes are represented by mode only; callers supply the concrete
 * `value` when constructing a full strategy.
 */
exports.validAllocationStrategyModeTuples = [
    ['INHERITED', 'ALL', 'ALL'],
    ['INHERITED', 'FIXED', 'ALL'],
    ['INHERITED', 'FIXED', 'FIXED'],
    ['INHERITED', 'FIXED', 'SINGLE'],
    ['INHERITED', 'UNBOUNDED', 'FIXED'],
    ['INHERITED', 'UNBOUNDED', 'SINGLE'],
    ['COMPOSED', 'FIXED', 'ALL'],
    ['COMPOSED', 'FIXED', 'FIXED'],
    ['COMPOSED', 'FIXED', 'SINGLE'],
    ['COMPOSED', 'UNBOUNDED', 'ALL'],
    ['COMPOSED', 'UNBOUNDED', 'FIXED'],
    ['COMPOSED', 'UNBOUNDED', 'SINGLE'],
];
//# sourceMappingURL=AllocationStrategy.js.map